import React, { useState } from 'react';
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from 'react19-google-recaptcha-v3';
import {
    sendContactMessage,
    ContactMessagePayload,
} from '../../services/contactServices';
import './Contact.css';

const RECAPTCHA_SITE_KEY =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;

/* ------------------------------------------------------------------
  Provider 内だけで useGoogleReCaptcha を呼ぶための内部コンポーネント
------------------------------------------------------------------ */
const ContactForm: React.FC = () => {
    /* フォーム入力値 */
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');

    /* UI 状態 */
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    /* ここで初めて Hook を呼ぶ -> Provider が上にあるので OK */
    const { executeRecaptcha } = useGoogleReCaptcha();

    /* 送信 */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); setSuccess('');

        if (!name || !email || !subject || !message) {
            setError('全てのフィールドを入力してください。');
            return;
        }
        if (!executeRecaptcha) {
            setError('reCAPTCHA の準備がまだできていません。');
            return;
        }

        setLoading(true);
        try {
            const token = await executeRecaptcha();
            if (!token) throw new Error('reCAPTCHA の検証に失敗しました。');

            // 送信ペイロードを LP と同じ型で組み立てて共通関数に渡す
            const payload: ContactMessagePayload = {
                name,
                email,
                subject,
                message,
                recaptcha_token: token,
                formType: 'contact_page',   // ★ 新しく追加するフォーム種別
            };
            await sendContactMessage(payload);
            setSuccess('お問い合わせを送信しました。ありがとうございました。');
            window.dataLayer?.push({ event: 'generate_lead', form_id: 'contact_page_form' });

            setName(''); setEmail(''); setSubject(''); setMessage('');
        } catch (err: any) {
            setError(
                err.response?.data?.detail ??
                err.message ??
                'お問い合わせの送信に失敗しました。'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {error && <p className="error-message">{error}</p>}
            {success && <p className="success-message">{success}</p>}

            <form className="contact-form" onSubmit={handleSubmit}>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="お名前" required />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="メールアドレス" required />
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="件名" required />
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="メッセージ" required />
                <button type="submit" className="btn" disabled={loading}>
                    {loading ? '送信中…' : '送信'}
                </button>
            </form>
        </>
    );
};

/* --------------------- ページ本体 --------------------- */
const Contact: React.FC = () => (
    <div className="contact-form-wrapper">
        <header className="contact-header">
            <a href="/" className="logo">
                <img src="/assets/images/logo.png" alt="autoChat AI" />
            </a>
        </header>

        <main id="contact" className="container">
            <h1 className="section-title">お問い合わせ</h1>
            <p>ご質問・ご要望は下記フォームよりお気軽にお送りください。</p>

            {/* Provider を一段外側に配置 */}
            <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
                <ContactForm />   {/* ← Provider 内なので Hook は安全 */}
            </GoogleReCaptchaProvider>

            <p className="contact-mail-link">
                または <a href="mailto:support@auto-chat.ai">support@auto-chat.ai</a> までご連絡ください。
            </p>
        </main>

        <footer>
            <div className="footer-logo">
                <img src="/assets/images/logo.png" alt="autoChat AI" />
            </div>
            <span>© 2025 autoChat AI Inc.</span>&nbsp;|&nbsp;
            <a href="/privacy-policy">プライバシーポリシー</a>&nbsp;|&nbsp;
            <a href="/terms-of-service">利用規約</a>&nbsp;|&nbsp;
            <a href="/tokushoho">特定商取引法に基づく表記</a>
        </footer>
    </div>
);

export default Contact;
